import { BrandChip } from '@/components/BrandChip';
import { type DataTableColumn, DataTable } from '@/components/DataTable';
import { PageHeader, PageShell } from '@/components/PageShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { allBrandsState, sharedNameplatesState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import type { Brand, BrandId, SharedNameplate } from '@rock/types';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SharedNameplateDialog } from './SharedNameplateDialog';

const LIST_PATH = '/products/shared-nameplates';

/**
 * The names two companies print, and how each is split and routed. A row's key in the URL opens the
 * wizard on that nameplate; `new` opens it empty.
 */
export const SharedNameplatesPage: FC = () => {
    const navigate = useNavigate();
    const { key } = useParams<{ key: string }>();
    const nameplates = useAsyncAtomValue(sharedNameplatesState, []);
    const brands = useAsyncAtomValue(allBrandsState, []);
    const brandsById = useMemo(() => new Map(brands.map((brand) => [brand.id, brand])), [brands]);
    const columns = useMemo(() => columnsFor(brandsById), [brandsById]);
    const selected = key && key !== 'new' ? nameplates.find((nameplate) => nameplate.key === key) : undefined;

    return (
        <PageShell>
            <PageHeader
                title="Shared Nameplates"
                description="Names two unrelated companies print. The bare name never resolves to one brand; equipment links to a qualified side, by space where the table says that is safe."
            >
                <Button onClick={() => navigate(`${LIST_PATH}/new`)}>Mark a name shared</Button>
            </PageHeader>
            <Card>
                <CardHeader>
                    <CardTitle>Nameplates</CardTitle>
                    <CardDescription>
                        {nameplates.length === 0 ? 'None yet.' : 'Tap a row to change its sides or routes.'}
                    </CardDescription>
                </CardHeader>
                {nameplates.length > 0 && (
                    <CardContent>
                        <DataTable
                            columns={columns}
                            rows={nameplates}
                            rowKey={(nameplate) => nameplate.key}
                            rowHref={(nameplate) => `${LIST_PATH}/${encodeURIComponent(nameplate.key)}`}
                        />
                    </CardContent>
                )}
            </Card>
            {(key === 'new' || selected) && (
                <SharedNameplateDialog
                    open
                    onOpenChange={(open) => !open && navigate(LIST_PATH)}
                    initialName={selected?.key ?? ''}
                />
            )}
        </PageShell>
    );
};

function columnsFor(brandsById: Map<BrandId, Brand>): DataTableColumn<SharedNameplate>[] {
    const chip = (id: BrandId) => {
        const brand = brandsById.get(id);
        return brand ? <BrandChip key={id} brand={brand} /> : <span key={id}>{id}</span>;
    };
    return [
        { key: 'key', header: 'Key', className: 'min-w-[140px] font-mono', cell: (nameplate) => nameplate.key },
        {
            key: 'sides',
            header: 'Sides',
            className: 'min-w-[280px]',
            cell: (nameplate) => <span className="flex flex-wrap gap-1">{nameplate.sides.map(chip)}</span>,
        },
        {
            key: 'routes',
            header: 'Routes',
            className: 'min-w-[280px]',
            cell: (nameplate) => (
                <ul className="text-sm">
                    {nameplate.routes.map((route) => (
                        <li key={route.spaceId}>
                            {route.spaceId} →{' '}
                            {route.brandId
                                ? (brandsById.get(route.brandId)?.canonicalName ?? route.brandId)
                                : 'unrouted'}
                            {route.note ? <span className="text-muted-foreground"> — {route.note}</span> : null}
                        </li>
                    ))}
                </ul>
            ),
        },
        { key: 'reason', header: 'Reason', className: 'min-w-[320px] text-sm', cell: (nameplate) => nameplate.reason },
    ];
}
